import {
  Stack, Text, Group, Button, Select, Badge, Card, Checkbox, Tabs, SimpleGrid, Skeleton, ThemeIcon, CloseButton, Alert,
} from '@mantine/core'
import { IconTemplate, IconShield, IconWand, IconServer, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useMemo } from 'react'
import {
  permissionsApi, groupByResource, templateGrantsNoTenants,
  type Permission, type PermissionTemplate,
} from '@/api/permissions'
import { permissionLabel, resourceLabel, buildResourceMetaIndex } from '@/api/permissionDisplay'
import { useAuthStore } from '@/store/auth'
import type { UserRow } from './UsersTable'
import { TenantScopeTab } from './TenantScopeTab'

interface UserDrawerPermissionsProps {
  user: UserRow
}

// TASK-11696 (fatia 5, tarefa 30): DEVOPS e DEVOPS-PLENO aprovam Terraform de
// producao, mas quem aprova de verdade e' a role `devops` (Task 28, backend
// fail-closed) — o perfil sozinho nao basta. Aqui e' so aviso, antes de
// salvar; o bloqueio de verdade mora no backend.
const DEVOPS_APPROVAL_TEMPLATE_NAMES = new Set(['devops', 'devops-pleno'])

function isDevopsApprovalTemplate(name: string | undefined | null): boolean {
  if (!name) return false
  return DEVOPS_APPROVAL_TEMPLATE_NAMES.has(name.trim().toLowerCase())
}

function hasDevopsRole(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => r.trim().toLowerCase() === 'devops')
}

export function UserDrawerPermissions({ user: targetUser }: UserDrawerPermissionsProps) {
  const qc = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)
  const refreshUser = useAuthStore((s) => s.refreshUser)
  const isEditingSelf = currentUser?.id === targetUser.id

  async function invalidateAndMaybeRefreshSelf() {
    qc.invalidateQueries({ queryKey: ['user-permissions', targetUser.id] })
    qc.invalidateQueries({ queryKey: ['users'] })
    qc.invalidateQueries({ queryKey: ['permission-templates'] })
    if (isEditingSelf) {
      await refreshUser()
    }
  }
  const [activeTab, setActiveTab] = useState<string>('global')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [template, setTemplate] = useState<string | null>(null)

  // O catalogo inteiro, nao so `items`: `resources` traz a meta de menu
  // (permission_resources), que e a fonte do rotulo de recurso. Antes o
  // `select` descartava esse campo e a tela caia no dicionario hardcoded.
  const { data: catalogo } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => permissionsApi.listPermissions(),
  })
  const allPermissions = catalogo?.items ?? []
  const resourceMeta = useMemo(
    () => buildResourceMetaIndex(catalogo?.resources ?? []),
    [catalogo],
  )

  const { data: permTemplates = [] } = useQuery<PermissionTemplate[]>({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
  })

  const { data: userPermsData, isLoading: loadingPerms } = useQuery({
    queryKey: ['user-permissions', targetUser.id],
    queryFn: () => permissionsApi.getUserPermissions(targetUser.id),
    enabled: !!targetUser.id,
  })

  // Catálogo de templates disponíveis para o usuário (união dos catálogos das
  // suas organizações). Quando `unrestricted`, o picker mostra todos.
  const { data: userOrgData } = useQuery({
    queryKey: ['user-organizations', targetUser.id],
    queryFn: () => permissionsApi.getUserOrganizations(targetUser.id),
    enabled: !!targetUser.id,
  })

  const isLinkedToTemplate = !!userPermsData?.applied_template_id

  // Auto-select first tenant tab with permissions when global is empty
  useEffect(() => {
    if (!userPermsData) return
    if (userPermsData.global_permissions.length > 0) return
    const firstTenantWithPerms = targetUser.tenants.find(
      (t) => (userPermsData.tenant_permissions?.[String(t.id)] ?? []).length > 0,
    )
    if (firstTenantWithPerms) {
      setActiveTab(String(firstTenantWithPerms.id))
    }
  }, [userPermsData, targetUser.tenants])

  // Sync selectedIds when user permissions load or tab changes
  useEffect(() => {
    if (!userPermsData) return
    const scope = activeTab === 'global'
      ? userPermsData.global_permissions
      : (userPermsData.tenant_permissions?.[activeTab] ?? [])
    const ids = new Set(
      allPermissions
        .filter((p) => scope.includes(`${p.resource}:${p.action}`))
        .map((p) => p.id)
    )
    setSelectedIds(ids)
  }, [userPermsData, activeTab, allPermissions])

  const applyTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!template) return
      await permissionsApi.applyTemplate(targetUser.id, Number(template))
    },
    onSuccess: async () => {
      await invalidateAndMaybeRefreshSelf()
      const tmpl = permTemplates.find((t) => String(t.id) === template)
      if (tmpl?.tenants?.length) {
        setActiveTab(String(tmpl.tenants[0].id))
      }
      notifications.show({ color: 'teal', title: 'Perfil aplicado', message: `Perfil "${tmpl?.name}" aplicado com sucesso.` })
      setTemplate(null)
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao aplicar perfil.' })
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: () => permissionsApi.unlinkTemplate(targetUser.id),
    onSuccess: async () => {
      await invalidateAndMaybeRefreshSelf()
      notifications.show({ color: 'teal', title: 'Desvinculado', message: 'Perfil removido. O usuário ficou sem permissões.' })
    },
    onError: (err: any) => {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao desvincular.' })
    },
  })

  const grouped = groupByResource(allPermissions)

  // Filtra os templates ofertados pelo catálogo das organizações do usuário.
  // `unrestricted` (ou ausência de dados) => comportamento atual (todos).
  const restricted = userOrgData?.unrestricted === false
  const availableIds = new Set((userOrgData?.available_templates ?? []).map((t) => t.id))
  const templateOptions = (restricted
    ? permTemplates.filter((t) => availableIds.has(t.id))
    : permTemplates
  ).map((t) => ({ value: String(t.id), label: t.name }))
  const selectedTemplate = permTemplates.find((t) => String(t.id) === template)
  const showsDevopsRoleWarning = isDevopsApprovalTemplate(selectedTemplate?.name)
    && !hasDevopsRole(targetUser.roles)

  if (loadingPerms) {
    return <Stack gap="sm"><Skeleton height={40} /><Skeleton height={200} /><Skeleton height={40} /></Stack>
  }

  return (
    <Stack gap="md">
      {/* Template selector */}
      <Card shadow="xs" withBorder p="sm">
        <Group gap="xs" mb="xs">
          <ThemeIcon size="sm" variant="light" color="teal"><IconTemplate size={14} /></ThemeIcon>
          <Text size="sm" fw={600}>Aplicar Perfil</Text>
        </Group>
        <Group gap="xs" align="end">
          <Select
            placeholder="Selecionar perfil..."
            data={templateOptions}
            description={restricted ? 'Perfis disponíveis nas organizações do usuário' : undefined}
            value={template}
            onChange={setTemplate}
            style={{ flex: 1 }}
            size="sm"
            searchable
            nothingFoundMessage="Nenhum template encontrado"
            clearable
          />
          <Button
            size="sm"
            leftSection={<IconWand size={14} />}
            disabled={!template}
            loading={applyTemplateMutation.isPending}
            onClick={() => {
              if (selectedTemplate && templateGrantsNoTenants(selectedTemplate)) {
                modals.openConfirmModal({
                  title: 'Perfil sem tenants',
                  children: (
                    <Alert color="yellow" variant="light">
                      O perfil <b>{selectedTemplate.name}</b> não concede nenhum tenant
                      (sem "todos os tenants", sem escopo por OU e sem tenants explícitos).
                      Aplicá-lo deixará o usuário <b>sem acesso efetivo</b>. Deseja continuar?
                    </Alert>
                  ),
                  labels: { confirm: 'Aplicar mesmo assim', cancel: 'Cancelar' },
                  confirmProps: { color: 'yellow' },
                  onConfirm: () => applyTemplateMutation.mutate(),
                })
                return
              }
              applyTemplateMutation.mutate()
            }}
          >
            Aplicar
          </Button>
        </Group>

        {showsDevopsRoleWarning && (
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} mt="xs">
            Este perfil só aprova pedidos de Terraform com a role DevOps — conceda a role na aba Roles.
          </Alert>
        )}

        {/* Template atual vinculado */}
        {isLinkedToTemplate && (
          <Group gap={6} mt="xs">
            <Text size="xs" c="dimmed">Perfil atual:</Text>
            <Badge
              size="sm"
              variant="light"
              color="teal"
              rightSection={
                <CloseButton
                  size="xs"
                  variant="transparent"
                  onClick={() => unlinkMutation.mutate()}
                  style={{ marginLeft: -2, marginRight: -4 }}
                />
              }
            >
              {userPermsData?.applied_template_name}
            </Badge>
          </Group>
        )}

        {/* Preview das permissoes do template selecionado (antes de aplicar) */}
        {selectedTemplate && (
          <Group gap={4} mt="xs" wrap="wrap">
            {selectedTemplate.permissions.map((p) => (
              <Badge key={p.id} size="xs" variant="light" color="teal">
                {resourceLabel(p.resource, resourceMeta.get(p.resource))}: {permissionLabel(p)}
              </Badge>
            ))}
          </Group>
        )}
      </Card>

      {/* Tabs: Global + per tenant */}
      <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? 'global')}>
        <Tabs.List>
          <Tabs.Tab value="global">Global</Tabs.Tab>
          {targetUser.tenants.map((t) => (
            <Tabs.Tab key={t.id} value={String(t.id)}>{t.name}</Tabs.Tab>
          ))}
          <Tabs.Tab value="scope" leftSection={<IconServer size={13} />}>
            Escopo de tenants
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* As tres faixas abaixo dizem o que a grade de checkboxes NAO consegue
          dizer. A grade mostra a base do perfil; o acesso real passa por
          superusuario, global_view e caps por ambiente/tenant. Sem isto, esta
          tela — que existe para o administrador CONFERIR acesso — mentia. */}
      {userPermsData?.is_superuser && (
        <Alert color="red" variant="light" icon={<IconShield size={16} />}>
          <b>Acesso irrestrito.</b> Este usuário é superusuário (flag <code>is_admin</code> ou
          função <code>admin</code>) e passa por curto-circuito em toda checagem de permissão.
          A lista abaixo não descreve o acesso dele.
        </Alert>
      )}

      {userPermsData?.global_read_all && !userPermsData?.is_superuser && (
        <Alert color="yellow" variant="light" icon={<IconServer size={16} />}>
          Este perfil tem <code>plataforma:global_view</code>: concede <b>leitura em qualquer
          tenant</b>, inclusive nos que não aparecem nas abas. Mutação continua seguindo o escopo.
        </Alert>
      )}

      {activeTab !== 'global' && activeTab !== 'scope' &&
        (userPermsData?.capped_tenants?.[activeTab]?.length ?? 0) > 0 && (
        <Alert color="orange" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text size="sm" fw={600} mb={4}>Base restrita neste tenant</Text>
          <Text size="xs">
            O perfil concede estas ações na base, mas uma restrição por ambiente/tenant as
            remove aqui:
          </Text>
          <Group gap={4} mt={6}>
            {userPermsData!.capped_tenants![activeTab].map((code) => (
              <Badge key={code} size="xs" variant="light" color="orange">{code}</Badge>
            ))}
          </Group>
        </Alert>
      )}

      {/* Tenant scope tab content */}
      {activeTab === 'scope' && (
        <TenantScopeTab userId={targetUser.id} />
      )}

      {/* Permission grid — somente leitura (ocultado na aba Tenants) */}
      {activeTab !== 'scope' && (
        <>
          <Stack gap="sm">
            {Object.entries(grouped).map(([resource, perms]) => (
              <Card key={resource} shadow="xs" withBorder p="sm" style={{ opacity: 0.8 }}>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="teal"><IconShield size={14} /></ThemeIcon>
                  <Text size="sm" fw={600}>{resourceLabel(resource, resourceMeta.get(resource))}</Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                  {perms.map((perm) => (
                    <Checkbox
                      key={perm.id}
                      checked={selectedIds.has(perm.id)}
                      readOnly
                      label={
                        <Stack gap={0}>
                          <Text size="sm">{permissionLabel(perm)}</Text>
                          {perm.description && <Text size="xs" c="dimmed">{perm.description}</Text>}
                        </Stack>
                      }
                      styles={{ input: { cursor: 'default' } }}
                    />
                  ))}
                </SimpleGrid>
              </Card>
            ))}
          </Stack>

          <Group justify="flex-end">
            <Badge size="sm" variant="light" color="gray">
              {userPermsData?.is_superuser
                ? 'acesso irrestrito'
                : `${selectedIds.size} permissões ativas`}
            </Badge>
          </Group>
        </>
      )}
    </Stack>
  )
}
